import { article } from "./articleHelpers";

const BLOG_ARTICLES = [
  article({
    slug: "personal-ai-assistant-everyday-life",
    title: "What Is a Personal AI Assistant for Everyday Life?",
    metaDescription:
      "Learn how a personal AI assistant like Manage360 unifies screen capture, media study, money, projects, todos, and chores—without replacing your free notes and calendar.",
    category: "Personal productivity",
    keywords: [
      "personal AI assistant",
      "everyday life admin",
      "all-in-one productivity app",
      "Manage360",
    ],
    publishedAt: "2026-01-10",
    readMinutes: 7,
    blocks: [
      {
        p: "Most people do not need another single-purpose app. They need one calm place where lectures, bills, tasks, and routines connect. A personal AI assistant in 2026 should reduce tab switching, not add a chat box you forget to open.",
      },
      {
        h2: "Problems a unified dashboard solves",
        p: [
          "You watch an online class in one tab, take notes in another, track expenses in a spreadsheet, and chase roommates for rent in a fourth app. Context is lost every time you switch.",
          "Manage360 keeps a free core workspace—notes, files, calendar, group bills—and adds optional AI Usage Credits for Screen AI, Media AI, and voice on money and tasks.",
        ],
      },
      {
        h2: "When AI helps vs when clicks are enough",
        ul: [
          "Screen AI: capture a lesson tab and ask questions later from saved screenshots.",
          "Media AI: upload slides or video for explanations per image or segment.",
          "Portfolio / Project / Todos / Daily Chore AI: voice when you have credits; full UI without.",
        ],
      },
      {
        h2: "Who benefits most",
        p: "Students, teachers, freelancers, and working professionals all hit the same friction: too many tools, too little time. Manage360 is built for anyone who wants structure first and smart capture second.",
      },
    ],
  }),
  article({
    slug: "screen-ai-lecture-notes-online-classes",
    title: "Screen AI for Online Classes: Capture Slides Without Retyping",
    metaDescription:
      "Use Screen AI to snapshot lecture tabs, merge audio with on-screen text, and build searchable summaries—ideal for online classes and webinars.",
    category: "Screen AI",
    keywords: [
      "Screen AI",
      "online class notes",
      "lecture capture",
      "screenshot summary AI",
    ],
    publishedAt: "2026-01-12",
    readMinutes: 8,
    blocks: [
      {
        p: "Online classes move fast. Pausing every slide to copy text breaks focus. Screen AI watches the tab you share, takes timed screenshots, and explains what was visible—maps, diagrams, bullet lists, and formulas included.",
      },
      {
        h2: "How Screen AI capture works",
        ul: [
          "Share the browser tab with your lesson—not the Screen AI page itself.",
          "Set a screenshot interval (for example every 10 seconds).",
          "Optional tab audio merges with on-screen text into one transcript.",
          "Finish to save a session you can reopen from history.",
        ],
      },
      {
        h2: "Ask about past sessions without reshare",
        p: "After class, use Ask about your past sessions to query summaries and saved screenshots. You do not need to share your screen again to remember what was on slide twelve.",
      },
      {
        h2: "Tips for better captures",
        ul: [
          "Maximize the slide or PDF viewer before sharing.",
          "Use This session only when asking about one lecture.",
          "Top up AI Usage Credits in Profile before long recordings.",
        ],
      },
    ],
  }),
  article({
    slug: "screen-ai-meeting-recap-without-typing",
    title: "Meeting Recaps With Screen AI (Less Typing, More Listening)",
    metaDescription:
      "Run Screen AI during internal meetings or training calls: capture visible decks and discussion context, then summarize without manual minutes.",
    category: "Screen AI",
    keywords: [
      "meeting notes AI",
      "Screen AI meeting",
      "automatic meeting summary",
      "training capture",
    ],
    publishedAt: "2026-01-14",
    readMinutes: 7,
    blocks: [
      {
        p: "Minute-taking during live meetings splits attention. Screen AI lets you listen while the tool snapshots shared decks and UI. After the call you get a structured summary and transcript you can edit.",
      },
      {
        h2: "Best use cases",
        ul: [
          "All-hands with slide decks",
          "Product demos where UI matters",
          "Training sessions with mixed speech and visuals",
        ],
      },
      {
        h2: "Privacy and consent",
        p: "Always follow your company policy and inform participants when recording or capturing screens. Manage360 stores sessions under your account; you control deletion from history.",
      },
    ],
  }),
  article({
    slug: "capture-slides-zoom-teams-google-meet",
    title: "Capture Slides From Zoom, Teams, or Meet With Screen AI",
    metaDescription:
      "Step-by-step mindset for capturing Zoom, Microsoft Teams, or Google Meet lessons with Manage360 Screen AI—tab audio optional, screenshots required.",
    category: "Screen AI",
    keywords: [
      "Zoom lecture notes",
      "Teams meeting capture",
      "Google Meet study",
      "share tab Screen AI",
    ],
    publishedAt: "2026-01-16",
    readMinutes: 6,
    blocks: [
      {
        p: "Video call apps differ, but the pattern is the same: share the tab that shows slides, not the gallery view. Screen AI reads what is on screen; garbled audio alone cannot replace a missed diagram.",
      },
      {
        h2: "Audio optional, visuals essential",
        p: "If tab audio is unavailable, screenshots still produce a screen-primary summary. That matches how many students watch muted or with poor call quality.",
      },
    ],
  }),
  article({
    slug: "ask-questions-about-past-screen-sessions",
    title: "Ask Questions About Past Screen Sessions (Screenshots Included)",
    metaDescription:
      "Manage360 Screen AI lets you query saved session summaries and screenshots—ideal for exam prep and follow-up questions after a live class.",
    category: "Screen AI",
    keywords: [
      "ask AI about lecture",
      "screen session Q&A",
      "study from screenshots",
    ],
    publishedAt: "2026-01-18",
    readMinutes: 5,
    blocks: [
      {
        p: "Traditional note apps store text you typed—not what was on screen. Screen AI keeps screenshot-backed history so you can ask what label appeared on the map or which step was highlighted in the lab demo.",
      },
      {
        h2: "How to use Ask",
        ul: [
          "Open the session in Screen AI history.",
          "Choose This session only for focused questions.",
          "Type a specific question; answers use summaries, transcripts, and images when available.",
        ],
      },
    ],
  }),
  article({
    slug: "media-ai-summarize-pdf-slides",
    title: "Media AI: Summarize PDFs, Slides, and Photos in One Upload",
    metaDescription:
      "Upload images or folders with Media AI—get per-file explanations for study decks, scanned notes, and reference photos without manual transcription.",
    category: "Media AI",
    keywords: [
      "Media AI",
      "summarize PDF slides",
      "AI image explanation",
      "study material upload",
    ],
    publishedAt: "2026-01-20",
    readMinutes: 7,
    blocks: [
      {
        p: "When material already exists as files—not a live tab—Media AI is the right tool. Upload JPEG, PNG, WebP, or GIF images; add a folder of lecture photos; get one analysis job per batch.",
      },
      {
        h2: "What you get per file",
        p: "Each image receives a clear explanation: what it shows, key labels, and why it matters for learning or work. Results stay in the sidebar for later review with previews when stored.",
      },
    ],
  }),
  article({
    slug: "media-ai-video-study-guide",
    title: "Turn Long Videos Into a Study Guide With Media AI",
    metaDescription:
      "Media AI splits videos into ~30 second segments with frame explanations—perfect for revision when you cannot rewatch a two-hour lecture.",
    category: "Media AI",
    keywords: [
      "video study guide AI",
      "Media AI video segments",
      "summarize lecture video",
    ],
    publishedAt: "2026-01-22",
    readMinutes: 8,
    blocks: [
      {
        p: "Rewatching entire videos before exams is slow. Media AI samples the timeline, explains each segment, and saves the full video for playback alongside text.",
      },
      {
        h2: "Supported formats",
        p: "MP4, WebM, and MOV uploads work on the server when ffmpeg is configured. Very large files may take longer; partial results save if processing stops mid-way.",
      },
    ],
  }),
  article({
    slug: "analyze-folder-of-study-images",
    title: "Analyze a Folder of Study Images in One Media AI Job",
    metaDescription:
      "Select an entire folder of chapter photos or lab stills—Media AI processes each image and keeps history organized by job title.",
    category: "Media AI",
    keywords: [
      "batch image analysis",
      "folder upload study",
      "Media AI batch",
    ],
    publishedAt: "2026-01-24",
    readMinutes: 5,
    blocks: [
      {
        p: "Phone photos of whiteboards and textbook pages pile up quickly. Folder upload deduplicates by name and size, then runs one titled analysis you can find later in history.",
      },
    ],
  }),
  article({
    slug: "voice-expense-tracking-india",
    title: "Voice Expense Tracking in India (Portfolio AI)",
    metaDescription:
      "Log income and expenses in Portfolio AI with voice commands when you have AI Usage Credits—built for INR-style personal finance worksheets, not bank feeds.",
    category: "Money & portfolio",
    keywords: [
      "voice expense tracking India",
      "Portfolio AI",
      "personal finance voice",
      "INR expense app",
    ],
    publishedAt: "2026-01-26",
    readMinutes: 6,
    blocks: [
      {
        p: "Spreadsheets work but typing every chai, UPI, and salary line is tedious. Portfolio AI folders organize spending; voice adds rows when credits are available.",
      },
      {
        h2: "Not a bank account",
        p: "Manage360 does not pull live bank data. You enter what you want to track—ideal for cash, UPI, and side income without linking accounts.",
      },
    ],
  }),
  article({
    slug: "portfolio-ai-personal-finance-workspace",
    title: "Portfolio AI: A Personal Finance Workspace (Not an Advisor)",
    metaDescription:
      "Understand Portfolio AI folders, totals, and voice queries—a simple worksheet for students, freelancers, and households tracking rupees manually.",
    category: "Money & portfolio",
    keywords: [
      "personal finance workspace",
      "expense folders",
      "income tracking app",
    ],
    publishedAt: "2026-01-28",
    readMinutes: 7,
    blocks: [
      {
        p: "Portfolio AI is for clarity, not investment advice. Group rent, food, travel, and projects; see totals; ask voice questions like how much you spent on food this month when AI credits allow.",
      },
    ],
  }),
  article({
    slug: "track-income-expenses-without-spreadsheet",
    title: "Track Income and Expenses Without a Spreadsheet",
    metaDescription:
      "Replace fragile spreadsheet tabs with Portfolio AI plus free notes and calendar—same login as your todos and chores on Manage360.",
    category: "Money & portfolio",
    keywords: [
      "expense tracker without Excel",
      "simple money tracker",
      "Manage360 portfolio",
    ],
    publishedAt: "2026-02-01",
    readMinutes: 6,
    blocks: [
      {
        p: "Spreadsheets break when you share the wrong version or delete a formula. A dashboard tab dedicated to money keeps structure and connects mentally to projects and chores in the same sidebar.",
      },
    ],
  }),
  article({
    slug: "voice-project-management-kanban",
    title: "Voice Project Management on a Kanban Board",
    metaDescription:
      "Move cards, set due dates, and update Project AI tasks by voice—hands-free project management when AI Usage Credits are available.",
    category: "Projects",
    keywords: [
      "voice project management",
      "Kanban voice",
      "Project AI",
    ],
    publishedAt: "2026-02-03",
    readMinutes: 6,
    blocks: [
      {
        p: "Project AI mirrors how teams think: columns, cards, deadlines. Voice is optional but powerful when you are walking between meetings and need to shift a card to Done without opening the laptop.",
      },
    ],
  }),
  article({
    slug: "project-ai-due-dates-board",
    title: "Project AI Boards With Due Dates (Students and Teams)",
    metaDescription:
      "Use Project AI for assignments, client work, or product lanes—due dates, columns, and voice updates in one Manage360 tab.",
    category: "Projects",
    keywords: [
      "Kanban due dates",
      "student project board",
      "task board app",
    ],
    publishedAt: "2026-02-05",
    readMinutes: 5,
    blocks: [
      {
        p: "Separate boards per course or client. Drag cards for status; use voice for quick captures when credits remain. Pair with Todos AI for daily micro-tasks.",
      },
    ],
  }),
  article({
    slug: "todos-ai-nested-checklists",
    title: "Todos AI: Nested Checklists for Real Life",
    metaDescription:
      "Break big goals into subtasks with Todos AI—complete, reorder, and optionally control lists by voice on Manage360.",
    category: "Todos",
    keywords: [
      "nested todo list",
      "Todos AI",
      "subtasks app",
    ],
    publishedAt: "2026-02-07",
    readMinutes: 5,
    blocks: [
      {
        p: "Flat todo apps hide complexity. Nested lists match how packing, studying, and launches actually work. Todos AI keeps parent/child structure visible.",
      },
    ],
  }),
  article({
    slug: "voice-todo-list-hands-free",
    title: "Hands-Free Todo Lists With Todos AI Voice",
    metaDescription:
      "Add and complete todos by voice when you have AI Usage Credits—useful while cooking, commuting, or between classes.",
    category: "Todos",
    keywords: [
      "voice todo list",
      "hands-free tasks",
      "Todos AI voice",
    ],
    publishedAt: "2026-02-09",
    readMinutes: 4,
    blocks: [
      {
        p: "Voice is not required. When you enable it, short commands reduce friction for capture-only moments where typing is awkward.",
      },
    ],
  }),
  article({
    slug: "daily-chore-ai-routine-reminders",
    title: "Daily Chore AI: Routines, Calendar, and Reminders",
    metaDescription:
      "Build recurring chores with SMS and email reminders in Daily Chore AI—check what is due today by voice or by opening the calendar.",
    category: "Daily chores",
    keywords: [
      "daily chore app",
      "routine reminders",
      "SMS chore reminder",
    ],
    publishedAt: "2026-02-11",
    readMinutes: 7,
    blocks: [
      {
        p: "Habits fail when they live only in your head. Daily Chore AI puts repeating tasks on a calendar and can nudge you by SMS or email using the phone and email on your profile.",
      },
    ],
  }),
  article({
    slug: "recurring-chores-sms-email-reminders",
    title: "Recurring Chores With SMS and Email Reminders",
    metaDescription:
      "How Manage360 Daily Chore AI sends reminders—what you need in Profile, and how carriers affect delivery in India and abroad.",
    category: "Daily chores",
    keywords: [
      "chore SMS reminder",
      "email routine reminder",
      "recurring tasks",
    ],
    publishedAt: "2026-02-13",
    readMinutes: 5,
    blocks: [
      {
        p: "Reminders only work if Profile contact details are correct. Delivery still depends on carriers and spam filters—plan a backup calendar view inside the app.",
      },
    ],
  }),
  article({
    slug: "split-bills-roommates-splitwise-alternative",
    title: "Split Bills With Roommates (Splitwise-Style Groups)",
    metaDescription:
      "Track who paid whom in Manage360 group bills—balances without forcing in-app settlement, ideal for roommates and trips.",
    category: "Group bills",
    keywords: [
      "split bills app",
      "Splitwise alternative",
      "roommate expenses",
    ],
    publishedAt: "2026-02-15",
    readMinutes: 6,
    blocks: [
      {
        p: "Group expenses are social, not just math. Record payments, see balances, settle offline. The app remembers who owes what so conversations stay fair.",
      },
    ],
  }),
  article({
    slug: "group-expense-tracking-india",
    title: "Group Expense Tracking for Trips and Flatmates in India",
    metaDescription:
      "Use Manage360 for UPI-settled trips, dinners, and rent splits—track in INR without linking bank accounts.",
    category: "Group bills",
    keywords: [
      "group expense India",
      "trip cost split",
      "flatmate bills",
    ],
    publishedAt: "2026-02-17",
    readMinutes: 5,
    blocks: [
      {
        p: "Most Indian groups settle via UPI outside any app. Manage360 records intent and balance; you pay friends directly.",
      },
    ],
  }),
  article({
    slug: "org-tree-3d-team-overview",
    title: "3D Org Tree: See People, Projects, and Todos Together",
    metaDescription:
      "Navigate an org map in 3D with linked projects and todos—local voice navigation on the org tree needs no AI Usage Credits.",
    category: "Org tree",
    keywords: [
      "3D org chart",
      "team overview",
      "org tree Manage360",
    ],
    publishedAt: "2026-02-19",
    readMinutes: 5,
    blocks: [
      {
        p: "Flat lists hide reporting lines. The org tree visualizes nodes with attached work. Voice navigation works locally without spending AI credits.",
      },
    ],
  }),
  article({
    slug: "one-app-notes-calendar-files",
    title: "Notes, Calendar, and Files in the Same Free Workspace",
    metaDescription:
      "Manage360 free tier includes notes, file storage, and calendar alongside AI tabs—one login for life admin basics.",
    category: "Workspace",
    keywords: [
      "notes calendar files app",
      "free productivity workspace",
    ],
    publishedAt: "2026-02-21",
    readMinutes: 5,
    blocks: [
      {
        p: "AI features are optional prepaid top-ups. Core tools remain usable without buying credits—important for students and anyone budgeting carefully.",
      },
    ],
  }),
  article({
    slug: "student-productivity-ai-dashboard",
    title: "Student Productivity: One Dashboard for Class, Money, and Tasks",
    metaDescription:
      "Students use Screen AI, Media AI, todos, projects, and portfolio tabs together—reduce app fatigue during semesters.",
    category: "Students",
    keywords: [
      "student productivity app",
      "study dashboard",
      "college organization",
    ],
    publishedAt: "2026-02-23",
    readMinutes: 7,
    blocks: [
      {
        p: "Semesters mix lectures, assignments, part-time income, and shared rent. One sidebar beats five disconnected apps with different logins.",
      },
    ],
  }),
  article({
    slug: "freelancer-income-projects-expenses",
    title: "Freelancers: Projects, Income, and Expenses in One Login",
    metaDescription:
      "Track client boards in Project AI, income in Portfolio AI, and daily actions in Todos—built for independent workers in India and globally.",
    category: "Freelancers",
    keywords: [
      "freelancer expense tracker",
      "client project board",
      "solo business admin",
    ],
    publishedAt: "2026-02-25",
    readMinutes: 6,
    blocks: [
      {
        p: "Freelancers juggle delivery and business ops. Manage360 separates client lanes on boards while portfolio folders show which client paid what.",
      },
    ],
  }),
  article({
    slug: "reduce-app-switching-life-admin",
    title: "Reduce App Switching for Life Admin",
    metaDescription:
      "Why context switching hurts productivity—and how a single Manage360 dashboard keeps capture, money, and tasks connected.",
    category: "Personal productivity",
    keywords: [
      "reduce app switching",
      "life admin",
      "productivity fatigue",
    ],
    publishedAt: "2026-02-27",
    readMinutes: 6,
    blocks: [
      {
        p: "Every switch costs a minute of refocus. Consolidating chores, calendar, notes, splits, and AI capture lowers mental overhead even if you still use AI selectively.",
      },
    ],
  }),
  article({
    slug: "ai-usage-credits-how-they-work",
    title: "AI Usage Credits: How They Work on Manage360",
    metaDescription:
      "Understand prepaid AI Usage Credits for Screen AI, Media AI, and voice—INR packs via Razorpay, optional on top of free core tools.",
    category: "Pricing",
    keywords: [
      "AI Usage Credits",
      "Manage360 pricing",
      "Razorpay credits",
    ],
    publishedAt: "2026-03-01",
    readMinutes: 6,
    blocks: [
      {
        p: "Credits are not a subscription for the whole app. They meter AI-heavy actions while notes, files, calendar, and group bills stay free.",
      },
      {
        h2: "Where credits are used",
        ul: [
          "Screen AI capture and ask",
          "Media AI uploads",
          "Voice on Portfolio, Project, Todos, and Daily Chore AI",
        ],
      },
    ],
  }),
  article({
    slug: "screen-ai-vs-note-taking-apps",
    title: "Screen AI vs Traditional Note Apps",
    metaDescription:
      "Compare Screen AI capture with manual note-taking—when screenshots plus AI beat typing every slide during live classes.",
    category: "Screen AI",
    keywords: [
      "Screen AI vs notes",
      "automatic lecture notes",
      "note taking comparison",
    ],
    publishedAt: "2026-03-03",
    readMinutes: 6,
    blocks: [
      {
        p: "Note apps reward typing speed. Screen AI rewards attention—you watch, it captures visuals. Best results combine both: AI summary plus your own highlights in Notes.",
      },
    ],
  }),
  article({
    slug: "media-ai-for-teachers-and-trainers",
    title: "Media AI for Teachers and Corporate Trainers",
    metaDescription:
      "Trainers upload slide decks and demo videos to Media AI—generate per-slide talking points and segment guides for learners.",
    category: "Media AI",
    keywords: [
      "trainer slide AI",
      "teaching material AI",
      "corporate training upload",
    ],
    publishedAt: "2026-03-05",
    readMinutes: 6,
    blocks: [
      {
        p: "After building materials offline, upload once to produce learner-friendly explanations. Pair with Screen AI for live delivery capture.",
      },
    ],
  }),
  article({
    slug: "remote-work-daily-planning-dashboard",
    title: "Remote Work Daily Planning With One Dashboard",
    metaDescription:
      "Remote workers combine calendar, todos, projects, and Screen AI meeting capture—one routine for distributed days.",
    category: "Remote work",
    keywords: [
      "remote work organization",
      "work from home dashboard",
      "daily planning app",
    ],
    publishedAt: "2026-03-07",
    readMinutes: 6,
    blocks: [
      {
        p: "Remote days blur without structure. Start in calendar and todos, capture meetings with Screen AI, track expenses in portfolio—same login end to end.",
      },
    ],
  }),
  article({
    slug: "working-professional-screen-and-media-ai",
    title: "Screen AI and Media AI for Working Professionals",
    metaDescription:
      "Professionals use Screen AI for webinars and Media AI for report PDFs—capture and file-based learning without student-only framing.",
    category: "Professionals",
    keywords: [
      "professional AI capture",
      "workshop slides AI",
      "business learning media",
    ],
    publishedAt: "2026-03-09",
    readMinutes: 5,
    blocks: [
      {
        p: "Continuing education and internal training look like student workflows but need professional tone. Manage360 prompts work for any learner context.",
      },
    ],
  }),
  article({
    slug: "teacher-lesson-prep-screen-capture",
    title: "Teachers: Prep and Review Lessons With Screen AI",
    metaDescription:
      "Teachers capture reference tabs and training videos, then ask questions from history when building tomorrow's lesson plan.",
    category: "Teachers",
    keywords: [
      "teacher lesson prep",
      "Screen AI education",
      "lesson planning tool",
    ],
    publishedAt: "2026-03-11",
    readMinutes: 5,
    blocks: [
      {
        p: "Build a library of captured reference sessions—maps, timelines, articles—and query them when writing worksheets without reopening ten bookmarks.",
      },
    ],
  }),
];

export const BLOG_CATEGORIES = [
  "All",
  ...Array.from(new Set(BLOG_ARTICLES.map((a) => a.category))).sort(),
];

export function getArticleBySlug(slug) {
  return BLOG_ARTICLES.find((a) => a.slug === slug) || null;
}

export function getRelatedArticles(articleRow, limit = 4) {
  return BLOG_ARTICLES.filter(
    (a) => a.category === articleRow.category && a.slug !== articleRow.slug
  ).slice(0, limit);
}

export function getAllBlogSlugs() {
  return BLOG_ARTICLES.map((a) => a.slug);
}

export default BLOG_ARTICLES;
